// Superficie tipada de la API (Req. 17–22). Agrupa por funcionalidad las
// llamadas que consumen las vistas, sobre un `ApiClient` inyectado.

import type { ApiClient } from './client.js';
import type {
  AdminTenantBilling,
  AdminUser,
  OwnerAccount,
  Plan,
  PlanInput,
  TenantUserAccount,
  ActivityLogEntry,
  ActivityProcessType,
  AdminAuditCategory,
  PermissionPreset,
  AgentProposal,
  AnalyticsResult,
  AudioResult,
  DealAlert,
  DealHunterConfig,
  DealHunterRun,
  TenantDealHunterConfig,
  DuplicateItem,
  Feature,
  IngestionJob,
  Location,
  Locale,
  LocaleConfig,
  AdminLocaleStats,
  MePermissions,
  PermissionLevel,
  QuickLookupResult,
  StoreLocatorResult,
  Record as VinylRecord,
  RecordChange,
  RecordDeletion,
  RecordImage,
  RecordDiff,
  ScoredCandidate,
  SearchResult,
  SyncJob,
  SyncJobItem,
  SyncItemOutcome,
  SyncRules,
  TopRecord,
  WishlistItem,
} from '../types.js';

/** Criterios de la búsqueda avanzada (objeto por campo). */
export type SearchCriteria = globalThis.Record<string, unknown>;

/** Fábrica de la API tipada a partir de un cliente. */
export function createApi(client: ApiClient) {
  return {
    // --- Permisos / sesión ---
    me: {
      permissions: () => client.request<MePermissions>('/me/permissions'),
      /** Documentos legales vigentes pendientes de aceptar por el usuario. */
      legalConsent: () => client.request<MyLegalConsent>('/me/legal-consent'),
      /** Acepta (re-acepta) las versiones legales indicadas. */
      acceptLegal: (versionIds: string[]) =>
        client.request<MyLegalConsent & { recorded: number }>('/me/legal-consent', {
          method: 'POST',
          body: { version_ids: versionIds },
        }),
      /**
       * Cambia el idioma de preferencia del usuario (i18n). Prevalece en el
       * próximo inicio de sesión. `null` reinicia a "hereda el de la cuenta".
       */
      setLocale: (locale: Locale | null) =>
        client.request<{ locale: Locale | null }>('/me/locale', {
          method: 'PUT',
          body: { locale },
        }),
      /** Marca el onboarding guiado como completado/omitido (no vuelve a salir). */
      completeOnboarding: () =>
        client.request<{ onboarding_completed: boolean }>('/me/onboarding/complete', {
          method: 'POST',
          body: {},
        }),
      /** Reinicia el onboarding guiado (se reactiva en el próximo acceso). */
      resetOnboarding: () =>
        client.request<{ onboarding_completed: boolean }>('/me/onboarding/reset', {
          method: 'POST',
          body: {},
        }),
      /**
       * Reclama (one-shot) la marca de activación GA4 `first_record_cataloged`
       * (RT-13). Devuelve `{ claimed }`: `true` SOLO la primera vez por usuario
       * de por vida. El cliente emite el evento de GA4 únicamente cuando
       * `claimed === true` (unicidad AUTORITATIVA en backend, no localStorage).
       */
      claimFirstRecordEvent: () =>
        client.request<{ claimed: boolean }>('/me/first-record-event', {
          method: 'POST',
          body: {},
        }),
      /** Estado vigente de las preferencias de consentimiento (cookies/analítica). */
      consentPreferences: () =>
        client.request<{ preferences: ConsentPreferenceState[] }>('/me/consent-preferences'),
      /** Actualiza o retira preferencias de consentimiento del usuario. */
      setConsentPreferences: (preferences: ConsentPreferenceState[]) =>
        client.request<{ recorded: number; preferences: ConsentPreferenceState[] }>(
          '/me/consent-preferences',
          { method: 'POST', body: { preferences } },
        ),
      /** Comprobante de consentimiento del usuario (PDF en base64 inline). */
      consentReceipt: () => client.request<ConsentReceipt>('/me/consent-receipt'),
      /** Paquete de datos personales del usuario (GDPR; JSON base64 inline). */
      dataExport: () => client.request<InlineFile>('/me/data-export'),
      /**
       * Deja rastro de un inicio de sesión exitoso en el log de actividad. Se
       * invoca tras autenticarse con Cognito (la autenticación es client-side y
       * el backend no tiene un hook de login). `method` es informativo
       * (`password` | `google` | `apple`). Best-effort: no bloquea la sesión.
       */
      loginEvent: (method?: string) =>
        client.request<{ recorded: boolean }>('/me/login-event', {
          method: 'POST',
          body: method ? { method } : {},
        }),
    },

    // --- Cuenta del tenant (avatar / perfil) ---
    account: {
      /** Perfil de la cuenta: nombre del catálogo + avatar. */
      getProfile: () =>
        client.request<{ tenant_name: string | null; avatar_url: string | null }>('/account'),
      /** Renombra el catálogo (tenant) de la PROPIA cuenta. */
      setTenantName: (tenantName: string) =>
        client.request<{ tenant_name: string }>('/account', {
          method: 'PUT',
          body: { tenant_name: tenantName },
        }),
      /** Avatar actual de la cuenta (URL de CloudFront) o null. */
      getAvatar: () => client.request<{ avatar_url: string | null }>('/account/avatar'),
      /** URL prefirmada para subir la imagen del avatar directo a S3. */
      avatarUploadUrl: (contentType: string) =>
        client.request<{ upload_url: string; s3_key: string; content_type: string }>(
          '/account/avatar/upload-url',
          { method: 'POST', body: { content_type: contentType } },
        ),
      /** Fija como avatar la imagen ya subida a S3 (por su clave). */
      setAvatar: (s3Key: string) =>
        client.request<{ avatar_url: string | null }>('/account/avatar', {
          method: 'PUT',
          body: { s3_key: s3Key },
        }),
      /** Quita el avatar (vuelve a mostrarse la letra inicial). */
      removeAvatar: () =>
        client.request<{ avatar_url: string | null }>('/account/avatar', { method: 'DELETE' }),
      /**
       * ELIMINA POR COMPLETO la cuenta (tenant) del propietario y TODA su
       * información (catálogo, imágenes, usuarios, configuración, facturación).
       * Si había un plan de pago activo, el backend cancela la suscripción antes.
       * Acción IRREVERSIBLE. Doble confirmación: `confirm=true` (query) + la frase
       * `confirm_text` (debe ser EXACTAMENTE la palabra que exige el backend).
       */
      deleteAccount: (confirmText: string) =>
        client.request<{
          deleted: boolean;
          had_paid_plan: boolean;
          subscription_canceled: boolean;
        }>('/account', {
          method: 'DELETE',
          query: { confirm: 'true' },
          body: { confirm_text: confirmText },
        }),
    },

    // --- Catálogo ---
    records: {
      list: (query: { limit?: number; offset?: number } = {}) =>
        client.request<{ items: VinylRecord[]; total: number }>('/records', { query }),
      get: (id: string) => client.request<VinylRecord>(`/records/${id}`),
      create: (body: Partial<VinylRecord>) =>
        client.request<VinylRecord>('/records', { method: 'POST', body }),
      // Edición manual (Req. 2.6): acepta ids o NOMBRES (artist_name/label_name/
      // genre_propio) que el backend resuelve con findOrCreate, más tracklist.
      update: (id: string, body: globalThis.Record<string, unknown>) =>
        client.request<VinylRecord>(`/records/${id}`, { method: 'PUT', body }),
      // Imágenes del release de Discogs asociado (para elegir carátula al editar).
      discogsImages: (id: string) =>
        client.request<{ items: string[]; reason?: string }>(`/records/${id}/discogs-images`),
      // Imágenes PROPIAS del disco (fotos subidas por el usuario a S3). Flujo:
      // createUploadUrl → PUT directo a S3 con el File → associate (crea la fila).
      images: {
        list: (id: string) => client.request<{ items: RecordImage[] }>(`/records/${id}/images`),
        createUploadUrl: (id: string, body: { content_type: string; kind?: string }) =>
          client.request<{
            upload_url: string;
            s3_key: string;
            content_type: string;
            kind: string;
          }>(`/records/${id}/images/upload-url`, { method: 'POST', body }),
        associate: (
          id: string,
          body: {
            s3_key: string;
            content_type?: string;
            byte_size?: number;
            kind?: string;
            make_primary?: boolean;
          },
        ) => client.request<RecordImage>(`/records/${id}/images`, { method: 'POST', body }),
        setPrimary: (id: string, imageId: string) =>
          client.request<{ record_id: string; primary_image_id: string }>(
            `/records/${id}/images/${imageId}/primary`,
            { method: 'POST', body: {} },
          ),
        remove: (id: string, imageId: string) =>
          client.request<{ deleted: boolean; s3_key: string }>(`/records/${id}/images/${imageId}`, {
            method: 'DELETE',
          }),
        reorder: (id: string, order: string[]) =>
          client.request<{ record_id: string; order: string[] }>(`/records/${id}/images/order`, {
            method: 'PUT',
            body: { order },
          }),
      },
      // Eliminación de un disco de la colección (Req.: historial de eliminación
      // con comentario). El `comment` es OBLIGATORIO: el backend lo exige y lo
      // registra en el historial de eliminación.
      remove: (id: string, comment: string) =>
        client.request<{ deleted: boolean; historyRecorded?: boolean }>(`/records/${id}`, {
          method: 'DELETE',
          body: { confirm: true, comment },
        }),
      // Historial de eliminación: de un disco concreto o de toda la colección.
      deletions: (id?: string, query: { limit?: number; offset?: number } = {}) =>
        client.request<{ items: RecordDeletion[]; limit: number; offset: number }>(
          id ? `/records/${id}/deletions` : '/records/deletions',
          { query },
        ),
      quickSearch: (
        q: string,
        opts: { sort?: string; dir?: string; page?: number; page_size?: number } = {},
      ) => client.request<SearchResult>('/records/search', { query: { q, ...opts } }),
      advancedSearch: (
        criteria: SearchCriteria,
        opts: { sort?: string; dir?: string; page?: number; page_size?: number } = {},
      ) =>
        client.request<SearchResult>('/records/search', {
          method: 'POST',
          body: { criteria, ...opts },
        }),
      // Autocompletar (Req. 3.1.a): sugiere valores existentes del catálogo por
      // prefijo para un campo (artista/título/sello/género/subgénero/nº cat.).
      suggest: (field: string, q: string, limit = 8) =>
        client.request<{ items: string[] }>('/records/suggest', {
          query: { field, q, limit },
        }),
      // Búsqueda por foto (Req. 3.9): OCR de la imagen subida + búsqueda local.
      searchByPhoto: (
        s3Key: string,
        opts: { sort?: string; dir?: string; page?: number; page_size?: number } = {},
      ) =>
        client.request<SearchResult & { ocr_text?: string; no_text?: boolean }>(
          '/records/search/photo',
          { method: 'POST', body: { s3_key: s3Key, ...opts } },
        ),
      changes: (id: string) => client.request<{ items: RecordChange[] }>(`/records/${id}/changes`),
      // Sincronización individual con Discogs.
      sync: (id: string, chosenReleaseId?: number) =>
        client.request<{ candidates: ScoredCandidate[]; diff: RecordDiff | null }>(
          `/records/${id}/sync`,
          { method: 'POST', body: { chosen_release_id: chosenReleaseId } },
        ),
      syncApply: (
        id: string,
        discogsReleaseId: number,
        mode: 'accept_all' | 'accept_selective' | 'reject',
        fields?: string[],
      ) =>
        client.request<{ record: VinylRecord; changes: RecordChange[]; sync_job_id: string }>(
          `/records/${id}/sync/apply`,
          { method: 'POST', body: { discogs_release_id: discogsReleaseId, mode, fields } },
        ),
      renumberPreview: (id: string) =>
        client.request<RenumberPreview>(`/records/${id}/renumber/preview`, {
          method: 'POST',
          body: {},
        }),
      renumberApply: (id: string) =>
        client.request<unknown>(`/records/${id}/renumber/apply`, { method: 'POST', body: {} }),
      // Exportación a Excel.
      exportGet: () => client.request<ExportResponse>('/records/export'),
      exportFiltered: (filter: SearchCriteria) =>
        client.request<ExportResponse>('/records/export', { method: 'POST', body: { filter } }),
    },

    // --- Géneros (catálogo de referencia + taxonomía configurable por tenant) ---
    genres: {
      list: (kind?: 'propio' | 'discogs') =>
        client.request<{ items: GenreOption[] }>('/genres', {
          query: kind ? { kind } : undefined,
        }),
      // Subgéneros ya usados en discos del género (texto libre derivado), para
      // poblar el desplegable dependiente del género en la ingesta.
      subgenres: (genreId: string) =>
        client.request<{ items: string[] }>(`/genres/${genreId}/subgenres`),
      // --- Administración de taxonomía ---
      create: (body: { name: string; kind?: 'propio' | 'discogs' }) =>
        client.request<GenreOption>('/genres', { method: 'POST', body }),
      update: (id: string, body: { name: string }) =>
        client.request<GenreOption>(`/genres/${id}`, { method: 'PUT', body }),
      remove: (id: string) =>
        client.request<{ deleted: boolean }>(`/genres/${id}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
      // Subgéneros del catálogo como entidades editables (con id).
      subgenreEntities: (genreId: string) =>
        client.request<{ items: Subgenre[] }>(`/genres/${genreId}/subgenres`, {
          query: { entities: 'true' },
        }),
      subgenreCreate: (genreId: string, body: { name: string }) =>
        client.request<Subgenre>(`/genres/${genreId}/subgenres`, { method: 'POST', body }),
      subgenreUpdate: (
        genreId: string,
        subId: string,
        body: { name?: string; genre_id?: string },
      ) =>
        client.request<Subgenre>(`/genres/${genreId}/subgenres/${subId}`, { method: 'PUT', body }),
      subgenreRemove: (genreId: string, subId: string) =>
        client.request<{ deleted: boolean }>(`/genres/${genreId}/subgenres/${subId}`, {
          method: 'DELETE',
        }),
      // Carga la propuesta inicial de taxonomía (idempotente).
      seedDefaults: () =>
        client.request<{ genres_created: number; subgenres_created: number }>(
          '/genres/seed-defaults',
          {
            method: 'POST',
          },
        ),
    },

    // --- Wishlist ---
    wishlist: {
      list: (query: WishlistQuery = {}) =>
        client.request<{ items: WishlistItem[]; total: number }>('/wishlist', {
          query: query as globalThis.Record<string, string | number | boolean | undefined>,
        }),
      get: (id: string) => client.request<WishlistItem>(`/wishlist/${id}`),
      create: (body: Partial<WishlistItem>) =>
        client.request<WishlistItem>('/wishlist', { method: 'POST', body }),
      update: (id: string, body: Partial<WishlistItem>) =>
        client.request<WishlistItem>(`/wishlist/${id}`, { method: 'PUT', body }),
      remove: (id: string) =>
        client.request<{ deleted: boolean }>(`/wishlist/${id}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
      setSuper: (id: string, value: boolean) =>
        client.request<WishlistItem>(`/wishlist/${id}/super`, {
          method: 'POST',
          body: { super_necesario: value },
        }),
    },

    // --- Repetidos ---
    duplicates: {
      list: () => client.request<{ items: DuplicateItem[]; total: number }>('/duplicates'),
      create: (body: Partial<DuplicateItem>) =>
        client.request<DuplicateItem>('/duplicates', { method: 'POST', body }),
      update: (id: string, body: Partial<DuplicateItem>) =>
        client.request<DuplicateItem>(`/duplicates/${id}`, { method: 'PUT', body }),
      remove: (id: string) =>
        client.request<{ deleted: boolean }>(`/duplicates/${id}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
    },

    // --- Top discos ---
    top: {
      list: () => client.request<{ items: TopRecord[]; total: number }>('/top'),
      create: (body: Partial<TopRecord>) =>
        client.request<TopRecord>('/top', { method: 'POST', body }),
      update: (id: string, body: Partial<TopRecord>) =>
        client.request<TopRecord>(`/top/${id}`, { method: 'PUT', body }),
      remove: (id: string) =>
        client.request<{ deleted: boolean }>(`/top/${id}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
    },

    // --- Ubicaciones ---
    locations: {
      list: () => client.request<{ items: Location[] } | Location[]>('/locations'),
      create: (body: Partial<Location>) =>
        client.request<Location>('/locations', { method: 'POST', body }),
      update: (id: string, body: Partial<Location>) =>
        client.request<Location>(`/locations/${id}`, { method: 'PUT', body }),
      remove: (id: string) =>
        client.request<{ deleted: boolean }>(`/locations/${id}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
      infer: (num: number) =>
        client.request<Location | null>('/locations/infer', { query: { num } }),
      /** Análisis de impacto del recálculo global de numeración (no aplica). */
      renumberPreview: () =>
        client.request<GlobalRenumberPreview>('/locations/renumber/preview', { method: 'POST' }),
      /** Aplica el recálculo global (requiere confirmación). */
      renumberApply: () =>
        client.request<GlobalRenumberApplyResult>('/locations/renumber/apply', {
          method: 'POST',
          query: { confirm: 'true' },
        }),
    },

    // --- Analítica ---
    analytics: {
      query: (body: AnalyticsQueryBody) =>
        client.request<AnalyticsResult>('/analytics/query', { method: 'POST', body }),
    },

    // --- Ingesta ---
    ingestion: {
      createUpload: (contentType: string, filename?: string) =>
        client.request<{ upload_url: string; s3_key: string }>('/ingestion/uploads', {
          method: 'POST',
          body: { content_type: contentType, filename },
        }),
      startJob: (body: {
        image_s3_keys?: string[];
        manual?: ManualHints;
        min_confidence?: number;
      }) =>
        client.request<{ id: string; status: string }>('/ingestion/jobs', { method: 'POST', body }),
      getJob: (id: string) => client.request<IngestionJob>(`/ingestion/jobs/${id}`),
      accept: (id: string, body: AcceptBody) =>
        client.request<AcceptResponse>(`/ingestion/jobs/${id}/accept`, { method: 'POST', body }),
      discard: (id: string) =>
        client.request<{ id: string; status: string }>(`/ingestion/jobs/${id}/discard`, {
          method: 'POST',
          body: {},
        }),
    },

    // --- Sincronización masiva ---
    sync: {
      createBulk: (body: { filter?: SearchCriteria; auto_apply?: boolean }) =>
        client.request<SyncJob>('/sync/jobs', { method: 'POST', body }),
      list: (query: { limit?: number; offset?: number } = {}) =>
        client.request<{ items: SyncJob[]; total: number }>('/sync/jobs', { query }),
      getJob: (id: string) => client.request<SyncJob>(`/sync/jobs/${id}`),
      items: (id: string, outcome?: SyncItemOutcome) =>
        client.request<{ items: SyncJobItem[]; counts: Record<SyncItemOutcome, number> }>(
          `/sync/jobs/${id}/items`,
          { query: outcome ? { outcome } : {} },
        ),
      pause: (id: string) =>
        client.request<SyncJob>(`/sync/jobs/${id}/pause`, { method: 'POST', body: {} }),
      resume: (id: string) =>
        client.request<SyncJob>(`/sync/jobs/${id}/resume`, { method: 'POST', body: {} }),
      cancel: (id: string) =>
        client.request<SyncJob>(`/sync/jobs/${id}/cancel`, { method: 'POST', body: {} }),
      revertJob: (id: string) =>
        client.request<unknown>(`/sync/jobs/${id}/revert`, { method: 'POST', body: {} }),
      revertChange: (changeId: string) =>
        client.request<unknown>(`/sync/changes/${changeId}/revert`, { method: 'POST', body: {} }),
    },

    // --- Consulta rápida ---
    quickLookup: {
      run: (body: {
        input_type: 'text' | 'catalog_nr' | 'photo';
        text?: string;
        s3_key?: string;
        artist?: string;
        title?: string;
      }) => client.request<QuickLookupResult>('/quick-lookup', { method: 'POST', body }),
      addToWishlist: (lookupId: string, body: Partial<WishlistItem> = {}) =>
        client.request<WishlistItem>(`/quick-lookup/${lookupId}/wishlist`, {
          method: 'POST',
          body,
        }),
    },

    // --- Cazador de ofertas ---
    dealHunter: {
      run: () =>
        client.request<{ run_id: string; deals_found: number }>('/deal-hunter/run', {
          method: 'POST',
          body: {},
        }),
      alerts: () => client.request<{ items: DealAlert[] }>('/deal-hunter/alerts'),
      runs: (params: { limit?: number; offset?: number } = {}) =>
        client.request<{ items: DealHunterRun[]; limit: number; offset: number }>(
          '/deal-hunter/runs',
          {
            query: params as globalThis.Record<string, number | undefined>,
          },
        ),
      // Reglas y frecuencia del cazador del propio tenant (config editable).
      getConfig: () => client.request<DealHunterConfig>('/config/deal-hunter'),
      updateConfig: (body: Partial<Omit<DealHunterConfig, 'last_run_at'>>) =>
        client.request<DealHunterConfig>('/config/deal-hunter', { method: 'PUT', body }),
    },

    // --- Localizador de Tiendas Físicas ---
    storeLocator: {
      /**
       * Busca tiendas de vinilos cercanas a la ubicación del usuario (lat/lng
       * capturadas por el navegador), rankeadas de más a menos populares.
       */
      find: (body: { lat: number; lng: number; radius_km?: number; limit?: number }) =>
        client.request<StoreLocatorResult>('/store-locator', { method: 'POST', body }),
      /** Radio de búsqueda por defecto del tenant (editable en caliente). */
      getConfig: () => client.request<{ search_radius_km: number }>('/store-locator/config'),
      /** Actualiza el radio de búsqueda por defecto (solo propietario). */
      setConfig: (searchRadiusKm: number) =>
        client.request<{ search_radius_km: number }>('/store-locator/config', {
          method: 'PUT',
          body: { search_radius_km: searchRadiusKm },
        }),
    },

    // --- Reconocimiento de audio ---
    audio: {
      createUpload: (contentType: string) =>
        client.request<{ upload_url: string; s3_key: string }>('/audio/uploads', {
          method: 'POST',
          body: { content_type: contentType },
        }),
      recognize: (body: { s3_key?: string; audio_base64?: string }) =>
        client.request<AudioResult>('/audio/recognize', { method: 'POST', body }),
    },

    // --- Administración de usuarios (propietario) ---
    // --- Configuración administrable ---
    config: {
      // Idiomas de la cuenta (i18n): idioma por defecto + idiomas habilitados.
      getLocale: () => client.request<LocaleConfig>('/config/locale'),
      setLocale: (body: { default_locale?: Locale; enabled_locales?: Locale[] }) =>
        client.request<{ default_locale: Locale; enabled_locales: Locale[] }>('/config/locale', {
          method: 'PUT',
          body,
        }),
      getDiscogsFormats: () =>
        client.request<{ options: string[]; selected: string[] }>('/config/discogs-formats'),
      setDiscogsFormats: (formats: string[]) =>
        client.request<{ selected: string[] }>('/config/discogs-formats', {
          method: 'PUT',
          body: { formats },
        }),
      // Reglas de auto-aplicación de la sincronización masiva (por tenant).
      getSyncRules: () => client.request<SyncRules>('/config/sync-rules'),
      setSyncRules: (rules: Partial<SyncRules>) =>
        client.request<SyncRules>('/config/sync-rules', { method: 'PUT', body: rules }),
      // Mapeo de columnas para la importación masiva (por tenant).
      getImportMapping: () => client.request<ImportMappingConfig>('/config/import-mapping'),
      setImportMapping: (mapping: Record<string, string>) =>
        client.request<ImportMappingConfig>('/config/import-mapping', {
          method: 'PUT',
          body: { mapping },
        }),
      // Jerarquía de ordenamiento para la numeración física (por tenant).
      getNumberingHierarchy: () =>
        client.request<NumberingHierarchyConfig>('/config/numbering-hierarchy'),
      setNumberingHierarchy: (hierarchy: SortLevel[]) =>
        client.request<NumberingHierarchyConfig>('/config/numbering-hierarchy', {
          method: 'PUT',
          body: { hierarchy },
        }),
      // Campos del formulario de disco: obligatorios estándar + personalizados
      // (por tenant). Impulsa la obligatoriedad dinámica y los campos extra.
      getRecordFields: () => client.request<RecordFieldConfig>('/config/record-fields'),
      setRecordFields: (body: { requiredStandard?: string[]; customFields?: CustomFieldDef[] }) =>
        client.request<RecordFieldConfig>('/config/record-fields', { method: 'PUT', body }),
      // --- Reglas de curación GENÉRICAS y PARAMÉTRICAS por tenant (Req. 24.j+) ---
      listCurationRules: (entity?: CurationRuleEntity) =>
        client.request<CurationRulesResponse>('/config/curation-rules', {
          query: entity ? { entity } : undefined,
        }),
      createCurationRule: (body: CurationRuleInput) =>
        client.request<CurationRule>('/config/curation-rules', { method: 'POST', body }),
      updateCurationRule: (id: string, body: Partial<CurationRuleInput>) =>
        client.request<CurationRule>(`/config/curation-rules/${id}`, { method: 'PUT', body }),
      deleteCurationRule: (id: string) =>
        client.request<{ deleted: boolean }>(`/config/curation-rules/${id}`, { method: 'DELETE' }),
      previewCurationRules: (body: {
        sample: string;
        entity?: CurationRuleEntity;
        rules?: CurationRuleInput[];
      }) =>
        client.request<{ input: string; output: string; changed: boolean }>(
          '/config/curation-rules/preview',
          { method: 'POST', body },
        ),
    },

    // --- Gestión de datos del catálogo (propietario): plantilla, importación
    // masiva y borrado total. ---
    catalog: {
      /** Plantilla .xlsx (solo encabezados) para la primera carga. */
      template: () => client.request<ExportResponse>('/catalog/template'),
      /** URL prefirmada para subir el .xlsx de importación al área del tenant. */
      importUploadUrl: (filename?: string) =>
        client.request<{ upload_url: string; s3_key: string }>('/catalog/import/upload-url', {
          method: 'POST',
          body: { filename },
        }),
      /** Previsualiza la importación (valida y resume, sin persistir). */
      importPreview: (body: { s3_key?: string; base64?: string }) =>
        client.request<ImportResult>('/catalog/import/preview', { method: 'POST', body }),
      /** Ejecuta la importación (persiste). */
      import: (body: { s3_key?: string; base64?: string }) =>
        client.request<ImportResult>('/catalog/import', { method: 'POST', body }),
      /** BORRA TODO el catálogo del tenant (requiere confirmación). */
      wipe: () =>
        client.request<{
          wiped: boolean;
          deletedByTable: Record<string, number>;
          deletedObjects: number;
        }>('/catalog', { method: 'DELETE', query: { confirm: 'true' } }),
    },

    admin: {
      listUsers: () => client.request<{ items: AdminUser[] }>('/admin/users'),
      invite: (body: {
        email: string;
        permissions?: Partial<Record<Feature, PermissionLevel>>;
        preset?: string;
      }) => client.request<AdminUser>('/admin/users', { method: 'POST', body }),
      setPermissions: (userId: string, permissions: Partial<Record<Feature, PermissionLevel>>) =>
        client.request<AdminUser>(`/admin/users/${userId}/permissions`, {
          method: 'POST',
          body: { permissions },
        }),
      revoke: (userId: string) =>
        client.request<{ deleted: boolean }>(`/admin/users/${userId}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
      // Actualiza el nombre visible de un usuario del tenant.
      updateUser: (userId: string, body: { display_name: string | null }) =>
        client.request<AdminUser>(`/admin/users/${userId}`, { method: 'PATCH', body }),
      // Borrado DEFINITIVO de un usuario (fila + Cognito), no solo baja lógica.
      deleteUser: (userId: string) =>
        client.request<{ deleted: boolean }>(`/admin/users/${userId}`, {
          method: 'DELETE',
          query: { confirm: 'true', hard: 'true' },
        }),
      // Crear un OWNER nuevo con su propio tenant (solo administrador del sistema).
      createOwner: (body: {
        email: string;
        display_name?: string;
        tenant_name?: string;
        plan_code?: string;
      }) => client.request<AdminUser>('/admin/owners', { method: 'POST', body }),
      // Borrado TOTAL de una cuenta de propietario y su tenant (irreversible;
      // solo administrador del sistema).
      deleteOwner: (ownerUserId: string) =>
        client.request<{ deleted: boolean }>(`/admin/owners/${ownerUserId}`, {
          method: 'DELETE',
          query: { confirm: 'true' },
        }),
      // Perfiles de permisos disponibles (para aplicar con un clic).
      presets: () => client.request<{ items: PermissionPreset[] }>('/admin/presets'),
      // Cuentas de propietario de TODOS los tenants (solo administrador global).
      listAccounts: () => client.request<{ items: OwnerAccount[] }>('/admin/accounts'),
      // Comprobante de consentimiento de un sujeto por email (PDF base64 inline).
      consentReceipt: (email: string) =>
        client.request<ConsentReceipt>(`/admin/consent-receipt?email=${encodeURIComponent(email)}`),
      // Paquete de datos personales de una cuenta de owner (JSON base64 inline).
      dataExport: (ownerUserId: string) =>
        client.request<InlineFile>(`/admin/owners/${ownerUserId}/data-export`),
      // Usuarios de un tenant concreto, con su estado de invitación.
      listTenantUsers: (tenantId: string) =>
        client.request<{ items: TenantUserAccount[] }>(`/admin/accounts/${tenantId}/users`),
      // Detalle de facturación de una cuenta: suscripción, fechas, IDs y enlaces
      // a Stripe, facturas y rastro de eventos. Solo administrador del sistema.
      getTenantBilling: (tenantId: string) =>
        client.request<AdminTenantBilling>(`/admin/tenants/${tenantId}/billing`),
      // Renombrar el catálogo (nombre del tenant) de una cuenta.
      renameTenant: (tenantId: string, tenantName: string) =>
        client.request<{ tenant_id: string; tenant_name: string }>(`/admin/accounts/${tenantId}`, {
          method: 'PATCH',
          body: { tenant_name: tenantName },
        }),
      // Catálogo completo de planes (activos e inactivos) para administración.
      listPlans: () => client.request<{ items: Plan[] }>('/admin/plans'),
      // Cambiar el plan de una cuenta (tenant) por su código de plan.
      setTenantPlan: (tenantId: string, planCode: string) =>
        client.request<{ tenant_id: string; plan: { code: string; name: string } }>(
          `/admin/tenants/${tenantId}/plan`,
          { method: 'POST', body: { plan_code: planCode } },
        ),
      // Crear un plan nuevo con sus límites (solo administrador del sistema).
      createPlan: (body: PlanInput) =>
        client.request<Plan>('/admin/plans', { method: 'POST', body }),
      // Editar la metadata y/o los límites de un plan por su id.
      updatePlan: (planId: string, body: PlanInput) =>
        client.request<Plan>(`/admin/plans/${planId}`, { method: 'PUT', body }),
      // Borrar un plan migrando sus cuentas al plan destino (por código).
      deletePlan: (planId: string, migrateToCode: string) =>
        client.request<{
          deleted: boolean;
          id: string;
          migrated: number;
          migrated_to: { code: string; name: string };
        }>(`/admin/plans/${planId}`, { method: 'DELETE', query: { migrate_to: migrateToCode } }),
      // Analítica GLOBAL cross-tenant (todos los tenants). Con `tenant_id` acota
      // a una cuenta; sin él, agrega sobre todas. Mismo contrato que la analítica
      // por cuenta (dimensiones/métricas), más el filtro de tenant.
      analytics: (body: AnalyticsQueryBody & { tenant_id?: string }) =>
        client.request<AnalyticsResult>('/admin/analytics', { method: 'POST', body }),
      // Reglas del cazador de ofertas de TODAS las cuentas (solo lectura).
      dealHunterConfigs: () =>
        client.request<{ items: TenantDealHunterConfig[] }>('/admin/deal-hunter-configs'),
      // Administración GLOBAL de idiomas (i18n): conteos por idioma + cuentas.
      localeStats: () => client.request<AdminLocaleStats>('/admin/locales'),
      // Ajusta el idioma por defecto y/o los idiomas habilitados de una cuenta.
      setTenantLocales: (
        tenantId: string,
        body: { default_locale?: Locale; enabled_locales?: Locale[] },
      ) =>
        client.request<{ tenant_id: string; default_locale: Locale; enabled_locales: Locale[] }>(
          `/admin/accounts/${tenantId}/locales`,
          { method: 'PUT', body },
        ),
      // Mesa de servicio GLOBAL: casos de soporte de TODAS las cuentas. Filtros
      // opcionales por cuenta y por estado.
      listSupport: (params: { tenant_id?: string; status?: string } = {}) =>
        client.request<{ items: AdminSupportTicket[]; total: number }>('/admin/support', {
          query: params as globalThis.Record<string, string | undefined>,
        }),
      // Detalle de un caso (de cualquier cuenta).
      getSupport: (id: string) => client.request<AdminSupportTicket>(`/admin/support/${id}`),
      // Cambia el estado de un caso (p. ej. cerrarlo tras responder por correo).
      setSupportStatus: (id: string, status: 'open' | 'in_progress' | 'resolved' | 'closed') =>
        client.request<AdminSupportTicket>(`/admin/support/${id}`, {
          method: 'PATCH',
          body: { status },
        }),
      // Log centralizado de actividad (todos los tenants/procesos). Filtro por
      // tipo de proceso, por tenant y paginación.
      listLogs: (
        params: {
          type?: ActivityProcessType;
          category?: AdminAuditCategory;
          tenant_id?: string;
          limit?: number;
          offset?: number;
        } = {},
      ) =>
        client.request<{
          items: ActivityLogEntry[];
          has_more: boolean;
          limit: number;
          offset: number;
          process_types: ActivityProcessType[];
          admin_audit_categories: AdminAuditCategory[];
        }>('/admin/logs', {
          query: params as globalThis.Record<string, string | number | undefined>,
        }),
    },

    // --- Logs de actividad del PROPIO tenant (vista del propietario) ---
    logs: {
      // Log del propio tenant; el backend lo acota con el tenant del actor.
      listMine: (
        params: {
          type?: ActivityProcessType;
          category?: AdminAuditCategory;
          limit?: number;
          offset?: number;
        } = {},
      ) =>
        client.request<{
          items: ActivityLogEntry[];
          has_more: boolean;
          limit: number;
          offset: number;
          process_types: ActivityProcessType[];
          admin_audit_categories: AdminAuditCategory[];
        }>('/logs', {
          query: params as globalThis.Record<string, string | number | undefined>,
        }),
    },

    // --- Facturación con Stripe (Stripe Billing): suscripción al plan. ---
    billing: {
      /** Catálogo de planes ACTIVOS (para la pantalla "elige tu plan"). */
      plans: () => client.request<{ items: Plan[] }>('/plans'),
      /** Estado de la suscripción de la cuenta (plan, estado, fin de ciclo). */
      subscription: () =>
        client.request<{ subscription: BillingSubscription | null }>('/billing/subscription'),
      /** Historial de facturas de la cuenta (desde Stripe). */
      invoices: () => client.request<{ invoices: BillingInvoice[] }>('/billing/invoices'),
      /** Pide a Stripe que envíe por correo una factura concreta de la cuenta. */
      emailInvoice: (invoiceId: string) =>
        client.request<{ invoice: BillingInvoice }>(
          `/billing/invoices/${encodeURIComponent(invoiceId)}/email`,
          { method: 'POST', body: {} },
        ),
      /**
       * Inicia el pago de un plan (mensual/anual). Devuelve la URL alojada de
       * Stripe Checkout a la que el frontend redirige con window.location.
       */
      checkout: (planCode: string, period: 'monthly' | 'yearly') =>
        client.request<{ url: string }>('/billing/checkout', {
          method: 'POST',
          body: { plan_code: planCode, period },
        }),
      /** Abre el portal de gestión de suscripción de Stripe (devuelve URL). */
      portal: () =>
        client.request<{ url: string }>('/billing/portal', { method: 'POST', body: {} }),
      /**
       * (Admin del sistema) Sincroniza un plan con Stripe: crea/actualiza su
       * Product y Prices y guarda los IDs. Botón "Sincronizar con Stripe".
       */
      syncPlan: (planId: string) =>
        client.request<{
          plan_id: string;
          stripe_product_id: string;
          stripe_price_id_monthly: string | null;
          stripe_price_id_yearly: string | null;
        }>(`/admin/plans/${planId}/stripe-sync`, { method: 'POST', body: {} }),
    },

    // --- Mesa de ayuda / soporte: temas comunes, casos de ayuda y feedback. ---
    support: {
      /** Catálogo de temas comunes (referencia para la UI). */
      topics: () => client.request<{ items: SupportTopic[] }>('/support/topics'),
      /** Casos del tenant (historial). */
      list: () => client.request<{ items: SupportTicket[]; total: number }>('/support'),
      /** Crea un caso de ayuda o de feedback; devuelve el caso con su número. */
      create: (body: CreateSupportTicket) =>
        client.request<{ ticket: SupportTicket; email_sent: boolean }>('/support', {
          method: 'POST',
          body,
        }),
    },

    // --- Guía de Usuario: materiales de apoyo en PDF (globales, descargables). ---
    guides: {
      /** Lista las guías disponibles (última versión de cada una) con su URL. */
      list: () => client.request<{ items: UserGuide[] }>('/guides'),
    },

    // --- Administración de DOCUMENTOS LEGALES versionados (solo system_admin) ---
    legalAdmin: {
      /** Todos los documentos legales con todas sus versiones. */
      list: () => client.request<{ items: LegalDocumentAdmin[] }>('/admin/legal'),
      /** Crea una versión en borrador de un documento. */
      createVersion: (body: {
        kind: LegalKind;
        version: string;
        title: string;
        summary?: string | null;
        locale?: string;
        sections: LegalSection[];
      }) => client.request<LegalVersion>('/admin/legal/versions', { method: 'POST', body }),
      /** Edita una versión en borrador. */
      updateVersion: (
        versionId: string,
        body: Partial<{
          version: string;
          title: string;
          summary: string | null;
          locale: string;
          sections: LegalSection[];
        }>,
      ) =>
        client.request<LegalVersion>(`/admin/legal/versions/${versionId}`, { method: 'PUT', body }),
      /** Publica un borrador (archiva la versión vigente del mismo documento). */
      publishVersion: (versionId: string) =>
        client.request<LegalVersion>(`/admin/legal/versions/${versionId}/publish`, {
          method: 'POST',
          body: {},
        }),
      /** Archiva una versión. */
      archiveVersion: (versionId: string) =>
        client.request<LegalVersion>(`/admin/legal/versions/${versionId}/archive`, {
          method: 'POST',
          body: {},
        }),
    },
  };
}

/** Tipo de documento legal. */
export type LegalKind = 'terms' | 'privacy' | 'legal_notice';

/** Estado de una versión legal. */
export type LegalVersionStatus = 'draft' | 'published' | 'archived';

/** Sección de un documento legal (encabezado + párrafo). */
export interface LegalSection {
  h: string;
  p: string;
}

/** Versión de un documento legal (vista de administración). */
export interface LegalVersion {
  id: string;
  document_id: string;
  version: string;
  status: LegalVersionStatus;
  locale: string;
  title: string;
  summary: string | null;
  sections: LegalSection[];
  content_hash: string;
  effective_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Documento legal con todas sus versiones (vista de administración). */
export interface LegalDocumentAdmin {
  id: string;
  kind: LegalKind;
  slug: string;
  title: string;
  versions: LegalVersion[];
}

/** Documento legal vigente (resumen) para el usuario. */
export interface MyLegalDocument {
  kind: LegalKind;
  slug: string;
  version_id: string;
  version: string;
  title: string;
  summary: string | null;
  locale: string;
  sections: LegalSection[];
  content_hash: string;
  effective_at: string | null;
  published_at: string | null;
}

/** Estado de consentimiento legal del usuario (pendientes de aceptar). */
export interface MyLegalConsent {
  pending: MyLegalDocument[];
  published?: MyLegalDocument[];
  needs_acceptance: boolean;
}

/** Estado vigente de una categoría de consentimiento (cookies/analítica). */
export interface ConsentPreferenceState {
  purpose: 'analytics' | 'session_replay' | 'marketing';
  granted: boolean;
}

/** Comprobante de consentimiento (PDF base64 inline). */
export interface ConsentReceipt {
  kind: 'inline';
  filename: string;
  content_type: string;
  base64: string;
  consent_count: number;
}

/** Archivo genérico devuelto inline (base64), p. ej. el export de datos. */
export interface InlineFile {
  kind: 'inline';
  filename: string;
  content_type: string;
  base64: string;
}

/** Estado de la suscripción de la cuenta (Stripe Billing). */
export interface BillingSubscription {
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  plan: {
    code: string;
    name: string;
    price_cents_monthly: number;
    price_cents_yearly: number;
    currency: string;
  } | null;
  /** Fecha de inscripción/alta de la suscripción (ISO). */
  started_at: string | null;
  /** Inicio del ciclo vigente (ISO/date). */
  current_period_start: string | null;
  /** Fin del ciclo vigente / fecha de renovación (ISO). */
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  has_customer: boolean;
}

/** Una factura de la cuenta (Stripe Billing). */
export interface BillingInvoice {
  id: string;
  number: string | null;
  status: string | null;
  amount_cents: number;
  currency: string;
  created: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

/** Público al que va dirigida una guía de usuario. */
export type GuideAudience = 'all' | 'owner' | 'admin';

/** Una guía de usuario (material de apoyo en PDF) lista para descargar. */
export interface UserGuide {
  slug: string;
  title: string;
  description: string;
  audience: GuideAudience;
  /** Número de versión disponible (1, 2, 3…). */
  version: number;
  /** URL pública de descarga (CloudFront). */
  url: string;
  /** Fecha de última actualización (ISO-8601) o null. */
  updated_at: string | null;
}

/** Tipo del objeto API (para inyectar por contexto). */
export type Api = ReturnType<typeof createApi>;

// --- Tipos de apoyo de las peticiones ---

export interface WishlistQuery {
  genre_id?: string;
  artist?: string;
  title?: string;
  super_necesario?: boolean;
  sort?: string;
  dir?: string;
  limit?: number;
  offset?: number;
}

export interface AnalyticsQueryBody {
  group_by: string[];
  metrics: string[];
  filters?: SearchCriteria;
  order_by?: string;
  dir?: 'asc' | 'desc';
  limit?: number;
  year_basis?: 'edition' | 'original';
}

/** Género de referencia para desplegables filtrables (Req. 3.5.a). */
export interface GenreOption {
  id: string;
  name: string;
  kind: 'propio' | 'discogs';
}

/** Subgénero del catálogo, ligado a un género padre. */
export interface Subgenre {
  id: string;
  name: string;
  genre_id: string;
}

/** Entidad a la que aplica una regla de curación genérica. */
export type CurationRuleEntity = 'artist' | 'label' | 'title';

/** Tipo de transformación paramétrica de una regla de curación genérica. */
export type CurationRuleKind =
  'strip_parentheses' | 'strip_brackets' | 'strip_trailing' | 'remove_text' | 'regex_replace';

/** Parámetros de una regla de curación genérica (según el `kind`). */
export interface CurationRuleParams {
  value?: string;
  pattern?: string;
  flags?: string;
  replacement?: string;
}

/** Regla de curación GENÉRICA y PARAMÉTRICA del tenant (Req. 24.j+). */
export interface CurationRule {
  id: string;
  entity_type: CurationRuleEntity;
  kind: CurationRuleKind;
  params: CurationRuleParams;
  enabled: boolean;
  priority: number;
  created_at?: string;
  updated_at?: string;
}

/** Cuerpo para crear/actualizar una regla de curación genérica. */
export interface CurationRuleInput {
  entity_type: CurationRuleEntity;
  kind: CurationRuleKind;
  params: CurationRuleParams;
  enabled?: boolean;
  priority?: number;
}

/** Respuesta del listado de reglas de curación genéricas (con metadatos). */
export interface CurationRulesResponse {
  items: CurationRule[];
  entities: CurationRuleEntity[];
  kinds: { kind: CurationRuleKind; label: string }[];
}

/** Tipo de caso de soporte. */
export type SupportTicketType = 'support' | 'feedback';

/** Tema común de la mesa de ayuda (referencia para la UI). */
export interface SupportTopic {
  key: string;
  label: string;
  description: string;
}

/** Un caso de soporte (ayuda o feedback) del tenant. */
export interface SupportTicket {
  id: string;
  case_number: number;
  type: SupportTicketType;
  category: string;
  subject: string;
  message: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  contact_email: string | null;
  contact_name: string | null;
  email_sent: boolean;
  created_at: string;
}

/** Cuerpo para crear un caso de soporte. */
export interface CreateSupportTicket {
  type: SupportTicketType;
  category: string;
  subject: string;
  message: string;
  contact_email?: string;
  contact_name?: string;
}

/** Caso de soporte enriquecido para la vista GLOBAL del administrador. */
export interface AdminSupportTicket extends SupportTicket {
  /** Cuenta (tenant) a la que pertenece el caso. */
  tenant_id: string;
  tenant_name: string | null;
  /** Email del usuario que creó el caso (si se conoce). */
  submitter_email: string | null;
  /** Etiqueta legible de la categoría/tema. */
  category_label: string;
  updated_at: string;
}

export interface ManualHints {
  artist?: string;
  title?: string;
  catalogNumber?: string;
}

export interface AcceptBody {
  record?: Partial<VinylRecord> | null;
  force_new?: boolean;
  remove_wishlist_item_id?: string;
}

export interface AcceptResponse {
  id?: string;
  status?: string;
  record_id?: string;
  wishlist_offer?: { reason: string; matches: WishlistItem[] } | null;
  removed_wishlist_item_id?: string | null;
  error?: string;
  match?: VinylRecord;
  actions?: string[];
}

export interface RenumberPreview {
  changes: {
    record_id: string;
    old_num: number | null;
    new_num: number | null;
    old_location?: string | null;
    new_location?: string | null;
    artist?: string | null;
    title?: string | null;
  }[];
  /** Número interno que se asignaría al disco nuevo. */
  assigned_number?: number | null;
  /** Segmento resuelto (o null si el género no tiene ubicación). */
  segment?: {
    genre_label?: string | null;
    subgenre_label?: string | null;
    range_start?: number;
    range_end?: number;
    free_after?: number;
    physical_place?: string | null;
  } | null;
  segment_full?: boolean;
  reorg?: { strategy: string; description: string } | null;
}

export interface ExportResponse {
  kind?: 'inline' | 's3';
  filename: string;
  content_type: string;
  base64?: string;
  download_url?: string;
  record_count?: number;
}

/** Un campo importable del modelo (con su etiqueta y si es requerido). */
export interface ImportField {
  field: string;
  label: string;
  required: boolean;
}

/** Configuración del mapeo de importación (campos + mapeo actual + default). */
export interface ImportMappingConfig {
  fields: ImportField[];
  mapping: Record<string, string>;
  default: Record<string, string>;
}

/** Clave de ordenamiento de la jerarquía de numeración. */
export type SortKey = 'genre' | 'subgenre' | 'artist' | 'year' | 'title';

/** Un nivel de la jerarquía de numeración: qué se compara y en qué sentido. */
export interface SortLevel {
  key: SortKey;
  direction: 'asc' | 'desc';
}

/** Configuración de la jerarquía de numeración (claves + actual + default). */
export interface NumberingHierarchyConfig {
  keys: { key: SortKey; label: string }[];
  hierarchy: SortLevel[];
  default: SortLevel[];
}

/** Tipo de dato de un campo personalizado del disco. */
export type CustomFieldType = 'text' | 'number' | 'boolean' | 'date' | 'select';

/** Pantalla donde se solicita un campo personalizado. */
export type CustomFieldScreen = 'add' | 'edit';

/** Definición de un campo personalizado del disco (por tenant). */
export interface CustomFieldDef {
  key: string;
  label: string;
  type: CustomFieldType;
  screens: CustomFieldScreen[];
  required: boolean;
  options?: string[];
}

/** Descriptor de un campo estándar configurable (etiqueta + default). */
export interface StandardFieldDescriptor {
  field: string;
  label: string;
  defaultRequired: boolean;
}

/**
 * Configuración de los campos del formulario de disco del tenant: qué campos
 * estándar son obligatorios y qué campos personalizados adicionales se piden.
 */
export interface RecordFieldConfig {
  standardFields: StandardFieldDescriptor[];
  requiredStandard: string[];
  customFields: CustomFieldDef[];
  defaultRequired: string[];
  customFieldTypes: CustomFieldType[];
  customFieldScreens: CustomFieldScreen[];
}

/** Un cambio de número old→new de un disco (recálculo). */
export interface RenumberChange {
  record_id: string;
  artist: string;
  title: string;
  old_num: number | null;
  new_num: number;
  old_location: string | null;
  new_location: string | null;
}

/** Impacto del recálculo global dentro de un segmento. */
export interface SegmentImpact {
  location_id: string;
  genre_label: string;
  subgenre_label: string | null;
  physical_place: string | null;
  rack: string | null;
  range_start: number;
  range_end: number;
  record_count: number;
  changed: number;
  overflow: boolean;
}

/** Análisis de impacto del recálculo global de numeración (preview). */
export interface GlobalRenumberPreview {
  total_changes: number;
  total_records: number;
  overflow_segments: number;
  segments: SegmentImpact[];
  changes: RenumberChange[];
}

/** Resultado de aplicar el recálculo global. */
export interface GlobalRenumberApplyResult {
  applied: boolean;
  operation_id: string;
  total_changes: number;
}

/** Issue de una fila durante la importación/previsualización. */
export interface ImportRowIssue {
  row: number;
  field?: string;
  message: string;
}

/** Resultado de importar (o previsualizar) el catálogo. */
export interface ImportResult {
  applied: boolean;
  totalRows: number;
  created: number;
  valid: number;
  skipped: number;
  issues: ImportRowIssue[];
  notEmptyWarning?: boolean;
  existingRecords?: number;
}

/** Propuesta del agente re-exportada para las vistas de ingesta. */
export type { AgentProposal };
